import { useState, useEffect } from 'react';
import { User, Gem, Clock, Copy, Check, Users, Crown, Zap, Sparkles, X } from 'lucide-react';
import { VERSION, BUILD_DATE, COMPANY_CN, COMPANY_EN } from '../version';

const API = '/api';
function getToken() { return localStorage.getItem('token'); }

const PACKAGES = [
  { id: 'std', name: '标准包', points: 500,  price: 39,  discount: '7.8折', icon: <Zap size={18} />,      color: 'blue',   desc: '约6段720p视频 或 125张图片', highlight: false },
  { id: 'adv', name: '进阶包', points: 1500, price: 159, discount: '7折',   icon: <Sparkles size={18} />, color: 'violet', desc: '整套剧：剧本+图片+视频全流程', highlight: true  },
  { id: 'pro', name: '旗舰包', points: 5000, price: 499, discount: '5折',   icon: <Crown size={18} />,    color: 'amber',  desc: '专业创作者，长期稳定使用',   highlight: false },
];

const PKG_COLOR = {
  blue:   { border: 'border-[#c7d8cf] dark:border-white/[0.12]', bg: 'bg-[#e7eee8] dark:bg-white/[0.08]', text: 'text-[#5e7c76] dark:text-[#9fc6bd]', btn: 'bg-[#5e7c76] hover:bg-[#52706a]' },
  violet: { border: 'border-[#c9bedc] dark:border-white/[0.12]', bg: 'bg-[#eee9de] dark:bg-white/[0.08]', text: 'text-[#6f6196] dark:text-[#b9afe7]', btn: 'bg-[#8c7db0] hover:bg-[#7d6ca3]' },
  amber:  { border: 'border-[#e0d3b9] dark:border-white/[0.12]', bg: 'bg-[#f2eadb] dark:bg-white/[0.08]', text: 'text-[#7f6b45] dark:text-[#d4c7a8]', btn: 'bg-[#9a8154] hover:bg-[#8b744a]' },
};

const LOG_TYPE_MAP = {
  registration:    { label: '注册赠送',   sign: '+' },
  purchase:        { label: '充值',       sign: '+' },
  referral_bonus:  { label: '推荐奖励',   sign: '+' },
  milestone_bonus: { label: '里程碑奖励', sign: '+' },
  consume_script:  { label: '剧本生成',   sign: '-' },
  consume_image:   { label: '图片生成',   sign: '-' },
  consume_video:   { label: '视频生成',   sign: '-' },
  canvas:          { label: '画布生成',   sign: '-' },
};

const TABS = [
  { key: 'account',  label: '账户' },
  { key: 'log',      label: '消费记录' },
  { key: 'referral', label: '邀请好友' },
];

function PurchaseModal({ pkg, onClose }) {
  const [done, setDone] = useState(false);
  const c = PKG_COLOR[pkg.color];
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm" onClick={onClose}>
      <div className="relative w-full max-w-sm rounded-2xl border border-[#ded6c8] bg-[#fbfaf6] p-6 dark:border-white/[0.08] dark:bg-[#15151b]" onClick={e => e.stopPropagation()}>
        <button onClick={onClose} className="absolute right-4 top-4 rounded-lg p-1.5 text-stone-400 hover:bg-stone-100 dark:hover:bg-white/[0.06]"><X size={16} /></button>
        <div className={`mb-4 inline-flex items-center gap-2 rounded-xl px-3 py-1.5 text-sm font-semibold ${c.bg} ${c.text}`}>{pkg.icon}{pkg.name} — {pkg.points} 点</div>
        <div className="mb-4 flex items-baseline gap-2">
          <span className="text-3xl font-bold text-gray-900 dark:text-gray-100">¥{pkg.price}</span>
          <span className={`rounded-full px-1.5 py-0.5 text-xs font-medium ${c.bg} ${c.text}`}>{pkg.discount}</span>
        </div>
        <div className={`mb-5 rounded-xl border p-4 ${c.border} ${c.bg}`}>
          <p className="mb-1 text-sm font-medium text-gray-700 dark:text-gray-300">支付方式</p>
          <p className="text-xs leading-relaxed text-gray-500">请通过微信/支付宝转账至管理员，备注「<strong className="text-gray-700 dark:text-gray-300">{pkg.name} + 注册邮箱</strong>」，管理员将在 24 小时内为您充值。</p>
          <div className="mt-3 rounded-lg border border-[#ded6c8] bg-[#fbfaf6] p-3 text-center dark:border-white/[0.08] dark:bg-white/[0.04]">
            <p className="mb-2 text-xs text-gray-400">联系管理员</p>
            <p className="font-mono text-sm font-medium text-gray-700 dark:text-gray-300">微信: xiaoying_admin</p>
          </div>
        </div>
        {!done ? (
          <button onClick={() => setDone(true)} className="w-full rounded-xl bg-[#8c7db0] py-2.5 text-sm font-medium text-white transition-colors hover:bg-[#7d6ca3]">我已完成支付，等待充值</button>
        ) : (
          <div className="w-full rounded-xl bg-[#e7eee8] py-3 text-center text-sm font-medium text-[#5e7c76] dark:bg-white/[0.08] dark:text-[#9fc6bd]">✓ 已收到，管理员将在 24 小时内处理</div>
        )}
      </div>
    </div>
  );
}

export default function Profile() {
  const [user, setUser] = useState(null);
  const [log, setLog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [purchaseModal, setPurchaseModal] = useState(null);
  const [copied, setCopied] = useState(false);
  const [tab, setTab] = useState('account');

  useEffect(() => {
    Promise.all([
      fetch(`${API}/users/me`, { headers: { Authorization: `Bearer ${getToken()}` } }).then(r => r.json()),
      fetch(`${API}/users/credits/log`, { headers: { Authorization: `Bearer ${getToken()}` } }).then(r => r.json()),
    ]).then(([u, l]) => {
      setUser(u);
      setLog(Array.isArray(l) ? l : []);
    }).catch(console.error).finally(() => setLoading(false));
  }, []);

  const copyReferralLink = async () => {
    const link = `${window.location.origin}/register?ref=${user.referral_code}`;
    try {
      await navigator.clipboard.writeText(link);
    } catch {
      // clipboard API 不可用（非聚焦/权限受限）时降级用 execCommand
      const ta = document.createElement('textarea');
      ta.value = link;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); } catch {}
      document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const freeCredits = user ? Math.max(0, (user.credits || 0) - (user.paid_credits || 0)) : 0;
  const referralProgress = user ? Math.min((user.referral_count || 0) % 3, 3) : 0;
  const nextMilestone = user ? (Math.floor((user.referral_count || 0) / 3) + 1) * 3 : 3;

  if (loading) return (
    <div className="flex min-h-[60vh] items-center justify-center">
      <div className="h-8 w-8 animate-spin rounded-full border-2 border-[#8c7db0]/30 border-t-[#8c7db0]" />
    </div>
  );

  return (
    <div className="min-h-full bg-[#f6f3ec] text-stone-900 dark:bg-[#0e0e10] dark:text-gray-100">
      {purchaseModal && <PurchaseModal pkg={purchaseModal} onClose={() => setPurchaseModal(null)} />}

      <header className="border-b border-[#ded6c8] bg-[#fbfaf6] px-8 pt-5 dark:border-white/[0.06] dark:bg-[#0c0c10]">
        <h1 className="text-[22px] font-bold">个人中心</h1>
        <div className="mt-4 flex gap-6">
          {TABS.map(t => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`pb-3 text-[14px] transition-colors ${tab === t.key
                ? 'border-b-2 border-[#8c7db0] font-medium text-stone-900 dark:text-gray-100'
                : 'text-stone-400 hover:text-stone-700 dark:text-gray-500 dark:hover:text-gray-300'}`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </header>

      <main className="px-6 py-6">
        {/* ── 账户 ── */}
        {tab === 'account' && (
          <div className="mx-auto flex max-w-[560px] flex-col gap-3.5">
            {/* 用户卡 */}
            <div className="flex items-center gap-3.5 rounded-xl border border-[#ded6c8] bg-white p-4 dark:border-white/[0.06] dark:bg-[#14141a]">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-[14px] bg-[#8c7db0] text-lg font-semibold text-white">
                {user?.username?.[0]?.toUpperCase() || <User size={22} />}
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-[15px] font-semibold">{user?.username}</p>
                  <span className={`rounded-md px-2 py-0.5 text-[10px] ${user?.role === 'admin' ? 'bg-[#f2eadb] text-[#7f6b45] dark:bg-white/[0.08] dark:text-[#d4c7a8]' : 'bg-stone-100 text-stone-500 dark:bg-white/[0.06] dark:text-gray-400'}`}>
                    {user?.role === 'admin' ? '管理员' : '普通用户'}
                  </span>
                </div>
                <p className="mt-0.5 truncate text-xs text-stone-400">{user?.email} · 注册于 {user?.created_at ? new Date(user.created_at).toLocaleDateString('zh-CN') : '—'}</p>
              </div>
            </div>

            {/* 点数卡 */}
            <div className="rounded-xl border border-[#ded6c8] bg-white p-4 dark:border-white/[0.06] dark:bg-[#14141a]">
              <div className="mb-3 flex items-end justify-between">
                <div>
                  <p className="mb-1 flex items-center gap-1.5 text-xs text-stone-400"><Gem size={14} className="text-[#6f6196] dark:text-[#b9afe7]" />我的点数</p>
                  <p className="text-[30px] font-semibold leading-none text-[#6f6196] dark:text-[#b9afe7]">{user?.credits ?? 0}</p>
                </div>
                <button onClick={() => document.getElementById('packages')?.scrollIntoView({ behavior: 'smooth' })}
                  className="rounded-[10px] bg-[#eee9de] px-4 py-2 text-[13px] font-medium text-[#6f6196] transition-colors hover:bg-[#e5dece] dark:bg-white/[0.08] dark:text-[#b9afe7]">
                  充值点数
                </button>
              </div>
              <div className="flex gap-2.5">
                <div className="flex-1 rounded-[9px] bg-[#f6f3ec] px-3 py-2 dark:bg-white/[0.04]">
                  <span className="text-[15px] font-semibold">{user?.paid_credits ?? 0}</span>
                  <span className="ml-1.5 text-[11px] text-stone-400">付费点数</span>
                </div>
                <div className="flex-1 rounded-[9px] bg-[#f6f3ec] px-3 py-2 dark:bg-white/[0.04]">
                  <span className="text-[15px] font-semibold">{freeCredits}</span>
                  <span className="ml-1.5 text-[11px] text-stone-400">赠送点数</span>
                </div>
              </div>
              <p className="mt-2.5 text-[10px] leading-relaxed text-stone-400">赠送点数仅限剧本和图片生成使用，视频生成需使用付费点数。</p>
            </div>

            {/* 充值套餐 */}
            <div id="packages">
              <p className="mb-2.5 text-xs text-stone-400">充值套餐</p>
              <div className="grid grid-cols-3 gap-2.5">
                {PACKAGES.map(pkg => {
                  const c = PKG_COLOR[pkg.color];
                  return (
                    <div key={pkg.id} className={`relative rounded-xl border bg-white p-3 text-center dark:bg-[#14141a] ${pkg.highlight ? 'border-2 border-[#8c7db0]' : 'border-[#ded6c8] dark:border-white/[0.06]'}`}>
                      {pkg.highlight && <span className="absolute -top-2 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-md bg-[#8c7db0] px-2 py-0.5 text-[9px] text-white">推荐</span>}
                      <div className={`mx-auto flex h-8 w-8 items-center justify-center rounded-lg ${c.bg} ${c.text}`}>{pkg.icon}</div>
                      <p className="mt-1.5 text-[12px] text-stone-500 dark:text-gray-400">{pkg.name}</p>
                      <p className="text-[16px] font-semibold">{pkg.points}<span className="text-[11px] font-normal text-stone-400">点</span></p>
                      <p className="my-1 text-[13px] font-semibold">¥{pkg.price}</p>
                      <button onClick={() => setPurchaseModal(pkg)} className={`w-full rounded-md py-1.5 text-[11px] text-white ${c.btn}`}>立即购买</button>
                    </div>
                  );
                })}
              </div>
              <p className="mt-3 text-center text-[11px] text-stone-400">购买即表示同意服务条款 · 点数永久有效 · 如有问题请联系客服</p>
            </div>
          </div>
        )}

        {/* ── 消费记录 ── */}
        {tab === 'log' && (
          <div className="mx-auto max-w-[640px]">
            <div className="mb-3 flex items-center justify-between">
              <p className="flex items-center gap-2 text-sm font-medium text-stone-600 dark:text-gray-300"><Clock size={15} className="text-[#6f6196] dark:text-[#b9afe7]" />消费记录</p>
              <span className="rounded-full bg-[#eee9de] px-2 py-0.5 text-[11px] text-stone-500 dark:bg-white/[0.06] dark:text-gray-400">{log.length} 条</span>
            </div>
            {log.length === 0 ? (
              <div className="py-24 text-center text-sm text-stone-400">暂无记录</div>
            ) : (
              <div className="overflow-hidden rounded-xl border border-[#ded6c8] bg-white dark:border-white/[0.06] dark:bg-[#14141a]">
                {log.map(item => {
                  const meta = LOG_TYPE_MAP[item.type] || { label: item.type, sign: '' };
                  const isPositive = meta.sign === '+';
                  return (
                    <div key={item.id} className="flex items-center justify-between border-b border-[#e4dccf] px-4 py-3 last:border-0 dark:border-white/[0.04]">
                      <div className="flex min-w-0 items-center gap-3">
                        <div className={`h-2 w-2 shrink-0 rounded-full ${isPositive ? 'bg-[#5e7c76]' : 'bg-stone-300 dark:bg-gray-600'}`} />
                        <div className="min-w-0">
                          <p className="text-sm text-stone-700 dark:text-gray-300">{meta.label}</p>
                          {item.note && <p className="max-w-[280px] truncate text-xs text-stone-400">{item.note}</p>}
                        </div>
                      </div>
                      <div className="shrink-0 text-right">
                        <p className={`text-sm font-semibold ${isPositive ? 'text-[#5e7c76] dark:text-[#9fc6bd]' : 'text-stone-500 dark:text-gray-400'}`}>{meta.sign}{item.amount} 点</p>
                        <p className="text-xs text-stone-400">{new Date(item.created_at).toLocaleDateString('zh-CN', { month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ── 邀请好友 ── */}
        {tab === 'referral' && (
          <div className="mx-auto flex max-w-[560px] flex-col gap-3.5">
            <div className="rounded-xl border border-[#ded6c8] bg-white p-4 dark:border-white/[0.06] dark:bg-[#14141a]">
              <div className="mb-3 flex items-center gap-2">
                <Users size={15} className="text-[#6f6196] dark:text-[#b9afe7]" />
                <p className="text-sm font-medium text-stone-700 dark:text-gray-300">推荐好友</p>
              </div>
              <div className="mb-1.5 flex items-center justify-between text-xs">
                <span className="text-stone-500">已推荐 {user?.referral_count ?? 0} 人</span>
                <span className="font-medium text-[#6f6196] dark:text-[#b9afe7]">距下次奖励 {nextMilestone - (user?.referral_count ?? 0)} 人</span>
              </div>
              <div className="mb-3 h-1.5 overflow-hidden rounded-full bg-stone-100 dark:bg-white/[0.06]">
                <div className="h-full rounded-full bg-[#8c7db0] transition-all duration-700" style={{ width: `${(referralProgress / 3) * 100}%` }} />
              </div>
              <div className="mb-3 space-y-0.5 text-xs text-stone-500">
                <p>• 每推荐 1 人成功注册 → <span className="font-medium text-[#6f6196] dark:text-[#b9afe7]">+10 点</span></p>
                <p>• 每满 3 人额外 → <span className="font-medium text-[#7f6b45] dark:text-[#d4c7a8]">+50 点</span></p>
              </div>
              <div className="rounded-xl border border-[#ded6c8] bg-[#fbfaf6] p-3 dark:border-white/[0.06] dark:bg-white/[0.03]">
                <p className="mb-1.5 text-[10px] text-stone-400">我的推荐码</p>
                <div className="flex items-center justify-between gap-2">
                  <code className="font-mono text-sm font-bold tracking-widest text-[#6f6196] dark:text-[#b9afe7]">{user?.referral_code || '—'}</code>
                  <button onClick={copyReferralLink} className="flex items-center gap-1 rounded-lg border border-[#ded6c8] bg-[#f4f1e9] px-2.5 py-1 text-xs text-stone-600 transition-colors hover:border-[#c9bedc] hover:text-[#6f6196] dark:border-white/[0.08] dark:bg-white/[0.05] dark:text-gray-400">
                    {copied ? <Check size={12} className="text-[#5e7c76]" /> : <Copy size={12} />}{copied ? '已复制' : '复制链接'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="mt-8 space-y-1 text-center">
          <p className="text-xs text-stone-400 dark:text-gray-500">{COMPANY_CN}</p>
          <p className="text-[11px] text-stone-300 dark:text-gray-600">{COMPANY_EN}</p>
          <p className="text-[11px] text-stone-300 dark:text-gray-600">小影 AI {VERSION} · {BUILD_DATE}</p>
        </div>
      </main>
    </div>
  );
}
